import React, { useEffect } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import {
  Boxes,
  MapPin,
  RefreshCw,
  Truck,
  Layers,
  FileSpreadsheet,
  ArrowRight
} from "lucide-react";
import "../styles/website.css";
import "../styles/ProductPages.css";

const WarehouseManagement = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "Bin & Location Mapping",
      description: "Define aisles, shelves, and bin coordinates for fast item picking.",
      icon: <MapPin size={22} />
    },
    {
      title: "Putaway Operations",
      description: "Suggest ideal storage locations for incoming items based on size, category, and weight.",
      icon: <Layers size={22} />
    },
    {
      title: "Picking & Packing",
      description: "Optimize picking lists, support batch/zone picking, and generate packing slips.",
      icon: <Boxes size={22} />
    },
    {
      title: "Stock Transfers",
      description: "Move stock between warehouses with digital transfer notes and transit logs.",
      icon: <RefreshCw size={22} />
    },
    {
      title: "Inventory Audits",
      description: "Support cycle counts, stocktake sync via mobile terminals, and log reconciliations.",
      icon: <FileSpreadsheet size={22} />
    },
    {
      title: "Dispatch & Logistics",
      description: "Integrate with delivery partners to print shipping labels and track shipments.",
      icon: <Truck size={22} />
    }
  ];

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow"></div>
        <div className="prod-hero-left">
          <span className="prod-badge">WAREHOUSE MANAGEMENT</span>
          <h1 className="prod-main-title">
            Optimized Space. <br />
            <span>Fluid Logisics.</span>
          </h1>
          <p className="prod-desc">
            Map warehouse shelves, optimize picking and packing operations, manage stock transfers,
            and coordinate dispatch logistics seamlessly.
          </p>
          <div className="prod-cta-group">
            <a href="#contact" className="prod-primary-btn">
              Request Demo <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right">
          <div className="prod-mockup-frame">
            <div className="mockup-header">
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">warehouse.gtelinq.com/dashboard</span>
            </div>
            <div className="mockup-content">
              <div className="mockup-sidebar">
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item active"></div>
                <div className="sidebar-item"></div>
              </div>
              <div className="mockup-main">
                <div className="mockup-bar"></div>
                <div className="mockup-cards">
                  <div className="mockup-card">
                    <h5>Active Zones</h5>
                    <span className="mockup-card-value">6</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "60%", background: "#55FF60" }}></div>
                    </div>
                  </div>
                  <div className="mockup-card">
                    <h5>Occupancy Ratio</h5>
                    <span className="mockup-card-value">74%</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "74%", background: "#55FF60" }}></div>
                    </div>
                  </div>
                </div>
                <div className="mockup-chart-container">
                  <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)" }}>ZONE OCCUPANCY ANALYSIS</span>
                  <div className="chart-bars">
                    <div className="chart-bar" style={{ height: "40px" }}></div>
                    <div className="chart-bar highlight" style={{ height: "65px" }}></div>
                    <div className="chart-bar" style={{ height: "55px" }}></div>
                    <div className="chart-bar" style={{ height: "30px" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">WAREHOUSE UTILITIES</span>
          <h2 className="prod-section-title">Maximize Shelf Space Efficiency</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="prod-testimonials-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">TESTIMONIALS</span>
          <h2 className="prod-section-title">Warehouse Success Stories</h2>
        </div>
        <div className="prod-testimonials-grid">
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "Mapping bin locations helped our warehouse staff pick orders 3 times faster.
              The putaway suggestions optimize our shelf usage beautifully."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">RD</div>
              <div className="prod-author-meta">
                <h5>Ramesh Deshpande</h5>
                <span>Facilities Manager, Nexus Corp</span>
              </div>
            </div>
          </div>
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "Transferring stock between our three regional hubs is simple. Printing shipping labels and
              packaging slips is fully integrated with logistics."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">AR</div>
              <div className="prod-author-meta">
                <h5>Alisha Rao</h5>
                <span>Logistics Head, PrimeSpaces Pvt Ltd</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>Can we map multiple physical locations?</h4>
            <p>Yes, GTE Warehouse supports setting up multiple warehouse directories, regions, zones, and custom bin grids.</p>
          </div>
          <div className="prod-faq-item">
            <h4>Is cycle counting supported?</h4>
            <p>Yes, you can schedule periodic inventory audits (cycle counting) for specific item zones without stopping warehouse operations.</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default WarehouseManagement;
